/**
 * CatalogChangesBanner — Phase 24 / Task 24.9 (DEC-100). Amber banner shown to
 * DISPATCH_MANAGER+ when hauler_catalog_change_reviews has PENDING rows:
 * "N wells have operator changes requiring your review". Dismissible per session
 * (reappears next login if still pending). Enabled only for gated roles.
 */
import * as React from 'react';
import { useReviewCount } from './hooks';

export function CatalogChangesBanner({
  enabled,
  onReview,
}: {
  readonly enabled: boolean;
  readonly onReview: () => void;
}): React.JSX.Element | null {
  const [dismissed, setDismissed] = React.useState(false);
  const { data } = useReviewCount(enabled);
  const pending = data?.pending ?? 0;

  if (!enabled || dismissed || pending === 0) return null;

  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '16px',
        padding: '10px 16px',
        margin: '12px 16px 0',
        backgroundColor: 'rgba(246, 182, 94, 0.13)',
        border: '1px solid var(--color-warning)',
        borderRadius: '12px',
        color: 'var(--color-text-primary)',
        fontSize: '14px',
      }}
    >
      <span>
        {pending} well{pending === 1 ? ' has' : 's have'} operator changes requiring your review.
      </span>
      <span style={{ display: 'flex', gap: '12px', flexShrink: 0 }}>
        <button
          type="button"
          onClick={onReview}
          style={{
            fontWeight: 600,
            color: 'var(--color-accent-blue)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          Review now →
        </button>
        <button
          type="button"
          onClick={(): void => setDismissed(true)}
          aria-label="Dismiss"
          style={{
            color: 'var(--color-text-tertiary)',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          ✕
        </button>
      </span>
    </div>
  );
}
